/**
 * Course Resources List Component
 * Displays downloadable files for the current topic (with inherited parent files)
 */
import React, { useState, useEffect } from 'react';
import { Download, FileText, File, Image, Archive, Code, Table, Presentation, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { courseResourcesService } from '@/services/courseResources.service';
import type { CourseFile, StudentFileListResponse, FileType } from '@/types/courseFile.types';

interface CourseResourcesListProps {
  courseId: string;
  topicId: string;
}

const FILE_TYPE_ICONS: Record<FileType, React.ElementType> = {
  pdf: FileText,
  document: File,
  spreadsheet: Table,
  presentation: Presentation,
  image: Image,
  archive: Archive,
  code: Code,
  text: FileText,
  other: File,
};

const FILE_TYPE_COLORS: Record<FileType, string> = {
  pdf: 'text-red-400 bg-red-400/20',
  document: 'text-blue-400 bg-blue-400/20',
  spreadsheet: 'text-green-400 bg-green-400/20',
  presentation: 'text-orange-400 bg-orange-400/20',
  image: 'text-purple-400 bg-purple-400/20',
  archive: 'text-yellow-400 bg-yellow-400/20',
  code: 'text-cyan-400 bg-cyan-400/20',
  text: 'text-gray-400 bg-gray-400/20',
  other: 'text-gray-400 bg-gray-400/20',
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function ResourceItem({ file, onDownload }: { file: CourseFile; onDownload: () => void }) {
  const Icon = FILE_TYPE_ICONS[file.file_type as FileType] || File;
  const colorClass = FILE_TYPE_COLORS[file.file_type as FileType] || FILE_TYPE_COLORS.other;

  const handleDownload = () => {
    onDownload();
    if (file.download_url) {
      window.open(file.download_url, '_blank');
    }
  };

  return (
    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-white/5 hover:border-indigo-500/30 transition-all group">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`p-2 rounded-lg flex-shrink-0 ${colorClass}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white group-hover:text-indigo-300 transition-colors truncate">
            {file.title}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {file.original_filename} &bull; {formatFileSize(file.file_size)}
          </p>
        </div>
      </div>
      <button
        onClick={handleDownload}
        className="p-2 bg-indigo-600/20 hover:bg-indigo-600/40 rounded-lg transition-all opacity-0 group-hover:opacity-100 flex-shrink-0 ml-2"
        title="Download"
      >
        <Download className="w-4 h-4 text-indigo-400" />
      </button>
    </div>
  );
}

function ResourceSection({
  title,
  files,
  onDownload,
  defaultExpanded = true
}: {
  title: string;
  files: CourseFile[];
  onDownload: (fileId: string) => void;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (files.length === 0) return null;

  return (
    <div className="mb-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full px-2 py-1.5 text-xs font-medium text-gray-400 hover:text-gray-300 transition-colors rounded hover:bg-white/5"
      >
        <span>{title} ({files.length})</span>
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {expanded && (
        <div className="space-y-2 mt-2">
          {files.map((file) => (
            <ResourceItem
              key={file.id || file._id}
              file={file}
              onDownload={() => onDownload(file.id || file._id || '')}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function CourseResourcesList({ courseId, topicId }: CourseResourcesListProps) {
  const [resources, setResources] = useState<StudentFileListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchResources = async () => {
      if (!courseId || !topicId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await courseResourcesService.getTopicFiles(courseId, topicId);
        setResources(data);
      } catch (err: any) {
        console.error('Error fetching resources:', err);
        // Don't show error to user - just silently fail
        setError(err.message || 'Failed to load resources');
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, [courseId, topicId]);

  const handleDownload = (fileId: string) => {
    courseResourcesService.trackDownload(fileId);
  };

  // Don't render if no resources
  if (!loading && (!resources || resources.total_count === 0)) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-slate-900/50 rounded-xl border border-white/10 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 bg-indigo-500/20 rounded-lg">
            <FileText className="w-5 h-5 text-indigo-400" />
          </div>
          <h3 className="font-semibold text-white">Resources</h3>
        </div>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !resources) {
    return null; // Silently fail - don't disrupt learning experience
  }

  return (
    <div className="bg-slate-900/50 rounded-xl border border-white/10 p-4 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="p-2 bg-indigo-500/20 rounded-lg">
          <FileText className="w-5 h-5 text-indigo-400" />
        </div>
        <div>
          <h3 className="font-semibold text-white">Resources</h3>
          <p className="text-xs text-gray-500">{resources.total_count} files available</p>
        </div>
      </div>

      {/* Topic-level files first (most specific) */}
      <ResourceSection
        title="This Topic"
        files={resources.topic_files}
        onDownload={handleDownload}
      />

      {/* Segment-level files */}
      <ResourceSection
        title="Segment Resources"
        files={resources.segment_files}
        onDownload={handleDownload}
        defaultExpanded={resources.topic_files.length === 0}
      />

      {/* Lesson-level files */}
      <ResourceSection
        title="Lesson Resources"
        files={resources.lesson_files}
        onDownload={handleDownload}
        defaultExpanded={false}
      />

      {/* Module-level files */}
      <ResourceSection
        title="Module Resources"
        files={resources.module_files}
        onDownload={handleDownload}
        defaultExpanded={false}
      />
    </div>
  );
}
